import React, { useState, useEffect } from 'react';
import { Download, Printer, Building2, Search, GraduationCap, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { exportToPDF } from '@/utils/groupReportPDF';

const GroupAdmissionReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(new Date().getFullYear() + '-01-01');
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCollege, setSelectedCollege] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [colleges, setColleges] = useState([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchColleges(); fetchReport(); }, []);

  const fetchColleges = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/groups/my-group`, { headers: { Authorization: `Bearer ${token}` } });
      setColleges(res.data.colleges || []);
    } catch (e) { console.error(e); }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/groups/my-group/reports/admissions?from_date=${fromDate}&to_date=${toDate}`;
      if (selectedCollege) url += `&college_id=${selectedCollege}`;
      if (selectedSession) url += `&session=${encodeURIComponent(selectedSession)}`;
      if (selectedCourse) url += `&course=${encodeURIComponent(selectedCourse)}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const exportToExcel = () => {
    if (!data?.all_admissions?.length) return toast.error('No data to export');
    const ws = XLSX.utils.json_to_sheet(data.all_admissions.map(a => ({
      'College': a.college_name, 'Student': a.full_name, 'Admission No': a.admission_number,
      'Course': a.course_name, 'Session': a.session, 'Date': a.admission_date,
      'Total Fee': a.total_fee, 'Admission Fee': a.admission_fee, 'Gender': a.gender, 'Type': a.type, 'Phone': a.phone
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Admission Report');
    XLSX.writeFile(wb, `Admission_Report_${fromDate}_${toDate}.xlsx`);
  };

  const exportPDF = () => {
    if (!data?.all_admissions?.length) return toast.error('No data to export');
    exportToPDF('Admission Report',
      ['College', 'Student', 'Adm No', 'Course', 'Session', 'Date', 'Fee', 'Type', 'Phone'],
      data.all_admissions.map(a => [a.college_name, a.full_name, a.admission_number, a.course_name, a.session, a.admission_date, `₹${(a.total_fee||0).toLocaleString('en-IN')}`, a.type, a.phone]),
      `Admission_Report_${fromDate}_${toDate}`,
      `Period: ${fromDate} to ${toDate} | Total: ${data.totals?.total_admissions||0} admissions`
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="group-admission-report">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admission Report</h1>
          <p className="text-sm text-gray-500">Student admissions across all colleges</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"><Download className="h-4 w-4" /> Excel</button>
          <button onClick={exportPDF} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"><FileText className="h-4 w-4" /> PDF</button>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"><Printer className="h-4 w-4" /> Print</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">From</label><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">To</label><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">College</label>
          <select value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Colleges</option>{colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Session</label>
          <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Sessions</option>{(data?.sessions || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Course</label>
          <select value={selectedCourse} onChange={e => setSelectedCourse(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Courses</option>{(data?.courses || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button onClick={fetchReport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"><Search className="h-4 w-4" /> Search</button>
      </div>

      {loading ? <div className="text-center py-12 text-gray-500">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-blue-500">
              <p className="text-xs text-gray-500">Total Admissions</p>
              <p className="text-3xl font-bold text-blue-700">{data.totals?.total_admissions || 0}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-green-500">
              <p className="text-xs text-gray-500">Total Course Fee</p>
              <p className="text-3xl font-bold text-green-700">₹{(data.totals?.total_fee || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>
          {data.colleges?.map(college => (
            <div key={college.college_id} className="bg-white rounded-xl shadow mb-4">
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-blue-600" /><h3 className="font-bold">{college.college_name}</h3><span className="text-xs text-gray-500">({college.total_admissions} admissions)</span></div>
                <span className="text-lg font-bold text-green-700">₹{(college.total_fee || 0).toLocaleString('en-IN')}</span>
              </div>
              {college.admissions?.length > 0 ? (
                <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-gray-50"><tr>
                  <th className="text-left p-3">Student</th><th className="text-left p-3">Admission No</th><th className="text-left p-3">Course</th><th className="text-left p-3">Session</th><th className="text-left p-3">Date</th><th className="text-left p-3">Type</th><th className="text-right p-3">Fee</th><th className="text-left p-3">Phone</th>
                </tr></thead><tbody className="divide-y">{college.admissions.map((a, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="p-3 font-medium">{a.full_name}</td>
                    <td className="p-3 font-mono text-xs">{a.admission_number}</td>
                    <td className="p-3">{a.course_name}</td>
                    <td className="p-3">{a.session}</td>
                    <td className="p-3">{a.admission_date}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${a.type === 'Lateral' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>{a.type}</span></td>
                    <td className="p-3 text-right font-medium">₹{(a.total_fee || 0).toLocaleString('en-IN')}</td>
                    <td className="p-3">{a.phone}</td>
                  </tr>
                ))}</tbody></table></div>
              ) : <p className="p-4 text-gray-400 text-sm">No admissions in this period</p>}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default GroupAdmissionReport;
